import React, { useState } from 'react';
import Icon from '../AppIcon';

/**
 * Reusable FAQ Component with Accordion
 * 
 * Features:
 * - Expand/collapse accordion functionality
 * - SEO-friendly HTML (no hidden content tricks)
 * - Accessibility (ARIA roles, keyboard navigation)
 * - Smooth animations
 * 
 * @param {Array} faqs - Array of FAQ objects with question and answer
 * @param {String} title - Section title (optional)
 * @param {String} description - Section description (optional)
 * @param {String} className - Additional CSS classes (optional)
 */
const FAQ = ({ 
  faqs = [], 
  title = "Frequently Asked Questions",
  description = "Get answers to common questions",
  className = "",
  defaultExpanded = null // Index of FAQ to expand by default
}) => {
  const [expandedIndex, setExpandedIndex] = useState(defaultExpanded);

  const toggleFAQ = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleFAQ(index);
    }
  };

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <section 
      id="faq" 
      className={`py-12 md:py-20 bg-gray-50 ${className}`}
      aria-label="Frequently Asked Questions"
    >
      <div className="container mx-auto px-4 md:px-6">
        {(title || description) && (
          <div className="text-center mb-8 md:mb-16">
            {title && (
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
                {title}
              </h2>
            )}
            {title && <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>}
            {description && (
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-6xl mx-auto px-4">
                {description}
              </p>
            )}
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="space-y-4" role="list">
            {faqs.map((faq, index) => {
              const isExpanded = expandedIndex === index;
              
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-100 transition-all duration-300 overflow-hidden"
                  role="listitem"
                >
                  <button
                    className={`w-full p-4 sm:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00d690] focus-visible:ring-opacity-50 focus-visible:ring-offset-2 rounded-t-xl transition-colors duration-200 ${
                      isExpanded 
                        ? 'bg-gray-50' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleFAQ(index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    aria-expanded={isExpanded}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                    type="button"
                  >
                    <div className="flex justify-between items-start gap-3 sm:gap-4">
                      <h3 
                        className="text-base sm:text-lg font-semibold text-gray-800 pr-2 sm:pr-4 leading-snug flex-1 hyphens-none"
                        id={`faq-question-heading-${index}`}
                      >
                        {faq.question}
                      </h3>
                      <div className="flex-shrink-0">
                        <Icon
                          name={isExpanded ? "ChevronUp" : "ChevronDown"}
                          size={20}
                          className="text-[#00d690] transition-opacity duration-300"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </button>

                  <div
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-heading-${index}`}
                    className={`transition-all duration-300 ease-in-out ${
                      isExpanded
                        ? 'max-h-[min(80vh,1200px)] opacity-100 overflow-y-auto'
                        : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                  >
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;

